(() => {
  "use strict";

  function initials(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "";
    if (parts.length === 1) {
      const letters = parts[0].replace(/[^\p{L}\p{N}]/gu, "");
      return (/^\p{Lu}{1,3}$/u.test(letters) ? letters.slice(0, 2) : letters.slice(0, 1)).toLocaleUpperCase("fr-FR");
    }
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toLocaleUpperCase("fr-FR");
  }

  function formatDate(value, precision = "jour") {
    const options = precision === "annee" ? { year: "numeric" } :
      precision === "mois" ? { month: "short", year: "numeric" } :
        { day: "numeric", month: "short", year: "numeric" };
    return new Intl.DateTimeFormat("fr-FR", options).format(new Date(`${value}T12:00:00`));
  }

  function dateTimeValue(value, precision = "jour") {
    return value.slice(0, precision === "annee" ? 4 : precision === "mois" ? 7 : 10);
  }

  function selectReply(review, max = 3) {
    return review.reponse && typeof review.reponse.texte === "string" &&
      review.reponse.texte.trim() && review.note <= max ? review.reponse : null;
  }

  function normalizeReviews(parsed) {
    return Array.isArray(parsed) ? parsed.filter((review) => {
      if (!review || typeof review.nom !== "string" || !review.nom.trim() ||
          typeof review.texte !== "string" ||
          typeof review.date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(review.date) ||
          !Number.isInteger(review.note) || review.note < 1 || review.note > 5) return false;
      const date = new Date(`${review.date}T12:00:00Z`);
      return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === review.date;
    }).map((review, index) => ({ ...review, date_precision: review.date_precision || "jour",
      reponse: review.reponse || null, originalIndex: index }))
      .sort((a, b) => b.date.localeCompare(a.date) || a.originalIndex - b.originalIndex) : [];
  }

  function columnCount(width) {
    if (width < 560) return 1;
    if (width < 800) return 2;
    if (width < 1100) return 3;
    return 4;
  }

  function computeBound(colHeights, cardsRects, allLoaded) {
    if (colHeights.length <= 1 || allLoaded) {
      return { bounded: false, height: null, hiddenIndexes: [] };
    }
    const height = cardsRects.reduce((bound, card) => (
      card.expanded ? Math.max(bound, card.top + card.height) : bound
    ), Math.min(...colHeights));
    return {
      bounded: true,
      height,
      hiddenIndexes: cardsRects.filter((card) => card.top + card.height > height + 1).map((card) => card.index)
    };
  }

  function pickFocusIndex(prevHidden, nowHidden, newIndexes) {
    const hidden = new Set(nowHidden);
    const candidates = [...new Set([...prevHidden, ...newIndexes])].filter((index) => !hidden.has(index));
    return candidates.length ? Math.min(...candidates) : -1;
  }

  if (typeof module !== "undefined") {
    module.exports = { initials, formatDate, dateTimeValue, selectReply, normalizeReviews, columnCount, computeBound, pickFocusIndex };
  }

  const instances = new WeakMap();

  function initSection(root) {
    if (root.dataset.mbbAvisReady === "true") return;
    const data = document.getElementById(root.id.replace(/^mbb-avis-/, "mbb-avis-data-"));
    if (!data) return;
    root.dataset.mbbAvisReady = "true";
    instances.set(root, () => { delete root.dataset.mbbAvisReady; });
    const viewport = root.querySelector("[data-viewport]");
    const grid = root.querySelector("[data-grid]");
    const empty = root.querySelector("[data-empty]");
    const more = root.querySelector("[data-more]");
    const count = root.querySelector("[data-count]");
    const load = root.querySelector("[data-load]");
    grid.replaceChildren();
    grid.hidden = false;
    viewport.hidden = false;
    empty.hidden = true;
    let reviews;
    try {
      reviews = normalizeReviews(JSON.parse(data.textContent));
    } catch {
      reviews = [];
    }
    if (!reviews.length) {
      empty.textContent = "Aucun avis pour le moment.";
      empty.hidden = false;
      viewport.hidden = true;
      grid.hidden = true;
      more.hidden = true;
      return;
    }

    const replySetting = Number(root.dataset.replyMaxRating);
    const replyMaxRating = Number.isInteger(replySetting) && replySetting >= 1 && replySetting <= 5 ? replySetting : 3;
    const pageSize = Number(root.dataset.pageSize);
    const PAGE_SIZE = Number.isInteger(pageSize) && pageSize >= 6 && pageSize <= 24 && pageSize % 6 === 0 ? pageSize : 12;
    const cards = [];
    let columns = [];
    let shown = 0;
    let hiddenIndexes = new Set();
    let resizeTimer;
    let observerFrame;
    let focusFrame;
    let animationTimer;
    let animationEnd;
    let targetHeight = null;
    let disposed = false;

    function stopAnimation() {
      window.clearTimeout(animationTimer);
      if (animationEnd) viewport.removeEventListener("transitionend", animationEnd);
      animationEnd = null;
      viewport.classList.remove("is-animating");
    }

    function applyBound(animate) {
      if (disposed) return;
      const bound = computeBound(
        columns.map((column) => column.offsetHeight),
        cards.map((card, index) => ({
          index, top: card.offsetTop, height: card.offsetHeight,
          expanded: !!card.querySelector('.mbb-avis__readmore[aria-expanded="true"]')
        })),
        shown >= reviews.length
      );
      hiddenIndexes = new Set(bound.hiddenIndexes);
      cards.forEach((card, index) => {
        if (hiddenIndexes.has(index)) {
          card.inert = true;
          card.setAttribute("aria-hidden", "true");
        } else {
          card.inert = false;
          card.removeAttribute("inert");
          card.removeAttribute("aria-hidden");
        }
      });
      const focusedArticle = document.activeElement?.closest(".mbb-avis__card");
      if (focusedArticle && grid.contains(focusedArticle) && focusedArticle.inert) {
        focusFallback();
      }
      viewport.classList.toggle("is-bounded", bound.bounded);
      const nextHeight = bound.bounded ? bound.height : grid.offsetHeight;
      // Observer notifications during the height transition must not cancel it.
      if (!animate && targetHeight !== null && Math.abs(targetHeight - nextHeight) <= 1) {
        if (!bound.bounded && !viewport.classList.contains("is-animating")) viewport.style.height = "";
        return;
      }
      const currentHeight = viewport.getBoundingClientRect().height;
      stopAnimation();
      targetHeight = nextHeight;
      if (animate && columns.length > 1 && !window.matchMedia("(prefers-reduced-motion: reduce)").matches && Math.abs(currentHeight - nextHeight) > 1) {
        viewport.style.height = `${currentHeight}px`;
        void viewport.offsetHeight;
        viewport.classList.add("is-animating");
        viewport.style.height = `${nextHeight}px`;
        animationEnd = (event) => {
          if (event && (event.target !== viewport || event.propertyName !== "height")) return;
          stopAnimation();
          if (!viewport.classList.contains("is-bounded")) viewport.style.height = "";
        };
        viewport.addEventListener("transitionend", animationEnd);
        animationTimer = window.setTimeout(() => animationEnd && animationEnd(), 600);
      } else if (!bound.bounded) {
        viewport.style.height = "";
      } else if (Math.abs(currentHeight - nextHeight) > 1 || !viewport.style.height) {
        viewport.style.height = `${nextHeight}px`;
      }
    }

    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => {
      if (disposed) return;
      window.cancelAnimationFrame(observerFrame);
      observerFrame = window.requestAnimationFrame(() => applyBound(false));
    }) : null;

    function element(tag, className, text) {
      const node = document.createElement(tag);
      node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    }

    function createReview(review, index) {
      const article = element("article", "mbb-avis__card");
      article.dataset.reviewIndex = String(index);
      const stars = element("div", "mbb-avis__stars");
      stars.setAttribute("role", "img");
      stars.setAttribute("aria-label", `Note : ${review.note} sur 5`);
      for (let number = 1; number <= 5; number += 1) {
        const star = element("span", number <= review.note ? "is-on" : "is-off", "★");
        star.setAttribute("aria-hidden", "true");
        stars.appendChild(star);
      }
      article.appendChild(stars);
      if (typeof review.titre === "string" && review.titre) {
        article.appendChild(element("h2", "mbb-avis__card-title", review.titre));
      }
      if (review.texte.trim()) {
        const body = element("p", "mbb-avis__text is-clamped", review.texte);
        body.id = `${root.id}-text-${index}`;
        article.appendChild(body);
      }
      const reply = selectReply(review, replyMaxRating);
      if (reply) {
        const response = element("div", "mbb-avis__reply");
        const heading = element("p", "mbb-avis__reply-head");
        heading.appendChild(element("span", "mbb-avis__reply-label", "Réponse de My Butcher Box"));
        if (reply.date) {
          const replyTime = element("time", "mbb-avis__date", formatDate(reply.date, reply.date_precision));
          replyTime.dateTime = dateTimeValue(reply.date, reply.date_precision);
          heading.appendChild(replyTime);
        }
        const replyBody = element("p", "mbb-avis__reply-text is-clamped", reply.texte);
        replyBody.id = `${root.id}-reply-${index}`;
        response.append(heading, replyBody);
        article.appendChild(response);
      }
      const footer = element("footer", "mbb-avis__foot");
      const avatar = element("span", "mbb-avis__avatar", initials(review.nom));
      avatar.setAttribute("aria-hidden", "true");
      const identity = element("span", "mbb-avis__identity");
      const date = element("time", "mbb-avis__date", formatDate(review.date, review.date_precision));
      date.dateTime = dateTimeValue(review.date, review.date_precision);
      identity.append(element("span", "mbb-avis__name", review.nom), date);
      footer.append(avatar, identity);
      article.appendChild(footer);
      return article;
    }

    function remeasureOverflow(article) {
      article.querySelectorAll(".mbb-avis__text, .mbb-avis__reply-text").forEach((body) => {
        let button = article.querySelector(`[aria-controls="${body.id}"]`);
        if (button && button.getAttribute("aria-expanded") === "true") return;
        body.classList.add("is-clamped");
        if (body.scrollHeight > body.clientHeight + 1) {
          if (button) return;
          button = element("button", "mbb-avis__readmore", "Lire la suite");
          button.type = "button";
          button.setAttribute("aria-expanded", "false");
          button.setAttribute("aria-controls", body.id);
          body.after(button);
        } else if (button) {
          if (document.activeElement === button) {
            article.tabIndex = -1;
            article.focus({ preventScroll: true });
          }
          button.remove();
        }
      });
    }

    function onReadMore(event) {
      const button = event.target.closest(".mbb-avis__readmore");
      if (!button || !grid.contains(button)) return;
      const body = document.getElementById(button.getAttribute("aria-controls"));
      if (!body || !grid.contains(body)) return;
      const expanded = button.getAttribute("aria-expanded") === "true";
      body.classList.toggle("is-clamped", expanded);
      button.setAttribute("aria-expanded", String(!expanded));
      button.textContent = expanded ? "Lire la suite" : "Réduire";
      applyBound(false);
    }

    function createColumns() {
      return Array.from({ length: columnCount(window.innerWidth) }, () => {
        const column = element("div", "mbb-avis__col");
        grid.appendChild(column);
        if (observer) observer.observe(column);
        return column;
      });
    }

    function appendCard(card) {
      const shortest = columns.reduce((best, current) => current.offsetHeight < best.offsetHeight ? current : best, columns[0]);
      shortest.appendChild(card);
      remeasureOverflow(card);
    }

    function focusFallback() {
      const target = load.hidden ? count : load;
      if (target === count) target.tabIndex = -1;
      target.focus({ preventScroll: false });
    }

    function relayoutExistingCards() {
      if (disposed) return;
      const focused = grid.contains(document.activeElement) ? document.activeElement : null;
      const focusedArticle = focused ? focused.closest(".mbb-avis__card") : null;
      stopAnimation();
      targetHeight = null;
      const oldColumns = columns;
      oldColumns.forEach((column) => {
        if (observer) observer.unobserve(column);
        column.style.display = "none";
      });
      columns = createColumns();
      cards.forEach(appendCard);
      oldColumns.forEach((column) => column.remove());
      applyBound(false);
      if (focusedArticle) {
        if (focusedArticle.inert) {
          focusFallback();
        } else {
          const target = focused.isConnected ? focused : focusedArticle;
          if (target === focusedArticle) target.tabIndex = -1;
          target.focus({ preventScroll: true });
        }
      }
    }

    function loadMore(animate) {
      const previouslyHidden = new Set(hiddenIndexes);
      const previousShown = shown;
      shown = Math.min(shown + PAGE_SIZE, reviews.length);
      const newIndexes = [];
      for (let index = previousShown; index < shown; index += 1) {
        const card = createReview(reviews[index], index);
        cards.push(card);
        newIndexes.push(index);
        appendCard(card);
      }
      count.textContent = `${shown} avis affichés sur ${reviews.length}`;
      more.hidden = false;
      load.hidden = shown >= reviews.length;
      applyBound(animate);
      if (animate) {
        window.cancelAnimationFrame(focusFrame);
        focusFrame = window.requestAnimationFrame(() => {
          if (disposed) return;
          const index = pickFocusIndex(previouslyHidden, hiddenIndexes, newIndexes);
          if (index !== -1) {
            cards[index].tabIndex = -1;
            cards[index].focus({ preventScroll: false });
          } else {
            focusFallback();
          }
        });
      }
    }

    columns = createColumns();
    loadMore(false);
    const onLoad = () => loadMore(true);
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(relayoutExistingCards, 150);
    };
    load.addEventListener("click", onLoad);
    grid.addEventListener("click", onReadMore);
    window.addEventListener("resize", onResize);
    instances.set(root, () => {
      disposed = true;
      if (observer) observer.disconnect();
      window.clearTimeout(resizeTimer);
      window.cancelAnimationFrame(observerFrame);
      window.cancelAnimationFrame(focusFrame);
      stopAnimation();
      load.removeEventListener("click", onLoad);
      grid.removeEventListener("click", onReadMore);
      window.removeEventListener("resize", onResize);
      delete root.dataset.mbbAvisReady;
    });
    if (document.fonts) document.fonts.ready.then(relayoutExistingCards);
  }

  function init() {
    // Shopify can execute the asset again when inserting a section.
    const lifecycleKey = Symbol.for("mbb.avis.lifecycle");
    if (document[lifecycleKey]) {
      document[lifecycleKey]();
      return;
    }
    function rootsWithin(scope) {
      const roots = [...scope.querySelectorAll("[data-mbb-avis]")];
      if (scope.matches && scope.matches("[data-mbb-avis]")) roots.unshift(scope);
      return roots;
    }
    document[lifecycleKey] = () => rootsWithin(document).forEach(initSection);
    document.addEventListener("shopify:section:load", (event) => {
      rootsWithin(event.target).forEach(initSection);
    });
    document.addEventListener("shopify:section:unload", (event) => {
      rootsWithin(event.target).forEach((root) => {
        const dispose = instances.get(root);
        if (dispose) dispose();
        instances.delete(root);
      });
    });
    document[lifecycleKey]();
  }

  if (typeof document !== "undefined") init();
})();
